import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiPalette, FiGlobe, FiType, FiImage, FiCheck, FiRefreshCw, FiExternalLink } = FiIcons;

const THEMES = [
  { id: 'indigo', name: 'Midnight Indigo', primary: '#6366f1', secondary: '#4338ca', gradient: 'from-indigo-600 to-indigo-800' },
  { id: 'emerald', name: 'Forest Emerald', primary: '#10b981', secondary: '#047857', gradient: 'from-emerald-600 to-emerald-800' },
  { id: 'rose', name: 'Sunset Rose', primary: '#f43f5e', secondary: '#be123c', gradient: 'from-rose-600 to-rose-800' },
  { id: 'amber', name: 'Golden Amber', primary: '#f59e0b', secondary: '#b45309', gradient: 'from-amber-600 to-amber-800' },
];

const CustomBranding = () => {
  const { t } = useLanguage();
  const [activeTheme, setActiveTheme] = useState(THEMES[0]);
  const [isSpinning, setIsSpinning] = useState(false);

  const handleThemeChange = (theme) => {
    setIsSpinning(true);
    setActiveTheme(theme);
    setTimeout(() => setIsSpinning(false), 600);
  };

  return (
    <section id="branding" className="py-24 bg-slate-50 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          
          {/* Left Side: Mockup Preview */}
          <div className="lg:w-1/2 relative order-2 lg:order-1">
            <div className="absolute -inset-10 bg-indigo-500/5 rounded-full blur-3xl -z-10"></div>
            
            <div className="bg-white rounded-[32px] border border-slate-200 shadow-2xl overflow-hidden relative">
              {/* Browser Header */}
              <div className="bg-slate-100 px-6 py-3 border-b border-slate-200 flex items-center gap-4">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300"></div>
                </div>
                <div className="flex-1 bg-white rounded-md h-6 px-3 flex items-center gap-2 border border-slate-200">
                  <SafeIcon icon={FiGlobe} className="w-3 h-3 text-slate-400" />
                  <span className="text-[10px] text-slate-400 font-medium tracking-tight">{t('brandingSection.browserUrl')}</span>
                </div>
              </div>

              {/* Event Page Preview */}
              <div className="p-8">
                <div className="flex justify-between items-center mb-10">
                  <motion.div 
                    animate={{ color: activeTheme.primary }}
                    className="flex items-center gap-2 font-black text-xl italic tracking-tighter"
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: activeTheme.primary }}>
                      Y
                    </div>
                    YOURBRAND
                  </motion.div>
                  <div className="flex gap-4">
                    <div className="w-8 h-2 bg-slate-100 rounded-full"></div>
                    <div className="w-8 h-2 bg-slate-100 rounded-full"></div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-4">
                    <motion.div 
                      initial={false}
                      animate={{ backgroundColor: activeTheme.primary + '10' }}
                      className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest"
                      style={{ color: activeTheme.primary }}
                    >
                      {t('brandingSection.invitation')}
                    </motion.div>
                    <h3 className="text-2xl font-bold text-slate-900 leading-tight">{t('brandingSection.galaTitle')}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{t('brandingSection.galaDesc')}</p>
                    <motion.button 
                      animate={{ backgroundColor: activeTheme.primary }}
                      className="w-full py-3 rounded-xl text-white text-xs font-bold shadow-lg shadow-indigo-500/20"
                    >
                      {t('brandingSection.ctaBook')}
                    </motion.button>
                  </div>
                  <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100 relative group">
                    <img 
                      src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&q=80&w=400" 
                      alt="Event" 
                      className="w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
                  </div>
                </div>

                {/* Ticket Mockup Overlay */}
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="bg-slate-900 rounded-2xl p-4 flex items-center justify-between border-l-4"
                  style={{ borderLeftColor: activeTheme.primary }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <SafeIcon icon={FiCheck} className="text-white w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-400 font-medium">{t('brandingSection.ticketReady')}</div>
                      <div className="text-xs text-white font-bold">{t('brandingSection.vipPass')}</div>
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded bg-white flex items-center justify-center p-1">
                    <div className="w-full h-full border border-slate-900 border-dashed"></div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Customizer Floating UI */}
            <div className="absolute -bottom-6 -right-6 md:-right-12 bg-white rounded-2xl p-5 shadow-2xl border border-slate-100 z-10 w-64">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('brandingSection.brandSettings')}</span>
                <motion.div animate={{ rotate: isSpinning ? 360 : 0 }} transition={{ duration: 0.6 }}>
                  <SafeIcon icon={FiRefreshCw} className="w-3 h-3 text-indigo-600" />
                </motion.div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] text-slate-500 mb-2 font-medium">{t('brandingSection.primaryTheme')}</div>
                  <div className="flex gap-2">
                    {THEMES.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => handleThemeChange(theme)}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          activeTheme.id === theme.id ? 'border-slate-900 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: theme.primary }}
                      />
                    ))}
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between text-[10px] font-bold text-slate-700">
                    <span>{t('brandingSection.customFont')}</span>
                    <span className="text-indigo-600">{t('brandingSection.fontName')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Content */}
          <div className="lg:w-1/2 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold uppercase tracking-wider mb-6">
              <SafeIcon icon={FiPalette} className="w-4 h-4" /> {t('brandingSection.badge')}
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">
              {t('brandingSection.title')} <br />
              <span className="text-gradient">{t('brandingSection.titleSpan')}</span>
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t('brandingSection.desc')}
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { icon: FiGlobe, title: t('brandingSection.features.domainsTitle'), desc: t('brandingSection.features.domainsDesc') },
                { icon: FiPalette, title: t('brandingSection.features.themeTitle'), desc: t('brandingSection.features.themeDesc') },
                { icon: FiType, title: t('brandingSection.features.emailTitle'), desc: t('brandingSection.features.emailDesc') },
                { icon: FiImage, title: t('brandingSection.features.logoTitle'), desc: t('brandingSection.features.logoDesc') }
              ].map((feature, i) => (
                <div key={i} className="group cursor-default">
                  <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-4 text-slate-600 group-hover:text-indigo-600 group-hover:border-indigo-200 transition-all">
                    <SafeIcon icon={feature.icon} className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1">{feature.title}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 flex items-center gap-6">
              <button className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-2 hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10">
                {t('brandingSection.ctaPlans')} <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomBranding;